import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

/* Iter97 — Standalone white-label buyer report.
   Designed to live INSIDE an iframe on a buyer's internal dashboard.
   No header, no nav, no auth. Authorized solely via the HMAC token in ?token=.
   Hard fail visibly if the token is missing or invalid.

   Note: this file is intentionally self-contained — no shared layout chrome —
   so the iframe is a tight, embeddable widget. ~540px tall fits the default
   `iframe_url` height returned by the mint endpoint. */

export default function EmbedBuyerReport() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const days = Number(params.get("days") || 30);

  const [data, setData] = useState(null);
  const [err, setErr]   = useState(null);

  useEffect(() => {
    if (!token) { setErr("Missing token"); return; }
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/embed/buyer-report?token=${encodeURIComponent(token)}&days=${days}`;
    fetch(url)
      .then(async (r) => {
        if (!r.ok) {
          const j = await r.json().catch(() => ({}));
          throw new Error(j.detail || `HTTP ${r.status}`);
        }
        return r.json();
      })
      .then(setData)
      .catch((e) => setErr(e.message || "Failed to load"));
  }, [token, days]);

  return (
    <div className="min-h-[540px] bg-white text-slate-900" data-testid="embed-buyer-report">
      <style>{`html, body, #root { background:#fff; margin:0; padding:0; }`}</style>

      {/* Header strip */}
      <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ConnexisDot />
          <div className="text-[12.5px] font-semibold text-slate-900">Connexis · Buyer report</div>
        </div>
        <div className="text-[10.5px] uppercase tracking-[0.2em] text-slate-400 font-semibold">
          {data ? `Last ${data.window_days} days` : ""}
        </div>
      </div>

      {/* Error state */}
      {err && (
        <div className="p-10 text-center" data-testid="embed-error">
          <div className="text-[13px] text-rose-600 font-semibold">Report unavailable</div>
          <div className="mt-1 text-[12px] text-slate-500">{err}</div>
          <div className="mt-4 text-[11px] text-slate-400">
            Need a new link? Re-mint your iframe URL from the Connexis dashboard.
          </div>
        </div>
      )}

      {/* Loading */}
      {!err && !data && (
        <div className="p-10" data-testid="embed-loading">
          <div className="grid grid-cols-3 gap-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 rounded-lg bg-slate-100 animate-pulse" />
            ))}
          </div>
        </div>
      )}

      {/* Data */}
      {data && (
        <div className="p-5 space-y-4" data-testid="embed-report-body">
          {/* KPI strip */}
          <div className="grid grid-cols-3 gap-3">
            <KPI label="Leads received"  value={data.total_leads.toLocaleString()} testid="embed-kpi-leads" />
            <KPI label="Total spend"     value={`£${data.total_spend_gbp.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} testid="embed-kpi-spend" />
            <KPI label="Avg price / lead" value={`£${data.avg_price_gbp.toFixed(2)}`} testid="embed-kpi-avg" />
          </div>

          {/* Wallet balance */}
          <div className="rounded-md border border-slate-200 px-4 py-3 flex items-center justify-between bg-slate-50/50">
            <span className="text-[11.5px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Wallet balance</span>
            <span className="text-[15px] font-semibold text-slate-900 font-mono" data-testid="embed-wallet">
              £{data.buyer.wallet_balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>

          {/* Sectors */}
          {data.top_sectors.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1.5">Top sectors</div>
              <div className="space-y-1.5">
                {data.top_sectors.map((s) => (
                  <div key={s.sector} className="flex items-center justify-between text-[12.5px]" data-testid={`embed-sector-${s.sector}`}>
                    <span className="capitalize text-slate-700">{(s.sector || "").replace(/-/g, " ")}</span>
                    <span className="font-mono font-semibold text-slate-900">{s.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Rejections */}
          {data.rejections.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1.5">Rejected leads (matched but not delivered)</div>
              <div className="space-y-1">
                {data.rejections.map((r) => (
                  <div key={r.reason} className="flex items-center justify-between text-[12px]">
                    <span className="text-slate-700 font-mono">{r.reason}</span>
                    <span className="font-mono font-semibold text-rose-700">{r.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="pt-2 text-[10.5px] text-slate-400 border-t border-slate-100 flex items-center justify-between">
            <span>Generated {new Date(data.generated_at).toLocaleString()}</span>
            <span>Powered by Connexis</span>
          </div>
        </div>
      )}
    </div>
  );
}

function KPI({ label, value, testid }) {
  return (
    <div className="rounded-md border border-slate-200 p-3" data-testid={testid}>
      <div className="text-[10.5px] uppercase tracking-[0.16em] text-slate-500 font-semibold">{label}</div>
      <div className="mt-1 text-[20px] font-semibold text-slate-900 font-mono tabular-nums leading-none">{value}</div>
    </div>
  );
}

function ConnexisDot() {
  return (
    <span className="inline-block size-3 rounded-full bg-gradient-to-br from-[#0055FF] to-[#5B8DFF]" aria-hidden="true" />
  );
}
